<script setup lang="ts">
import '../assets/style/fonts.css'

import { Icon } from '@iconify/vue'
import { onMounted, reactive, ref } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { useRouter } from 'vue-router'
import { useTheme } from 'vuetify'

import BetaSignupSection from '@/components/BetaSignupSection.vue'
import GenderedLogo from '@/components/GenderedLogo.vue'
import { useUtil } from '@/composables/util'

/** Height for the logo carousel and individual logos on the landing page. */
const CAROUSEL_HEIGHT = '120px'

const logger = useLogger()
const { loginPublicUser } = useUtil(logger)

interface IndexState {
  username: string
  password: string
  loginState: boolean
  invalidFeedback: string
}

const router = useRouter()

interface Props {
  loginName?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  loginName: null,
})

const state = reactive<IndexState>({
  invalidFeedback: '',
  loginState: true,
  password: '',
  username: '',
})

onMounted(() => {
  const vueTheme = useTheme()
  if (props.loginName) {
    state.username = props.loginName
  }
  // state.setToast()
  vueTheme.global.name.value = 'light' // XXX why?
})

import indexPhoto2 from '../assets/images/shutterstock_536408482.jpg'
import indexPhoto1 from '../assets/images/shutterstock_1716030475.jpg'

const indexPhoto1Url = ref(indexPhoto1)
const indexPhoto2Url = ref(indexPhoto2)
</script>

<template>
  <div>
    <v-sheet class="pt-4 pb-10" color="grey-lighten-4" elevation="0">
      <v-row class="pt-4 pr-6" justify="end">
        <v-col v-if="true" md="3">
          <v-btn
            align="right"
            block
            color="primary"
            @click="
              () => {
                loginPublicUser(router)
              }
            "
          >
            <span class="mr-3"><v-icon>mdi-voice</v-icon></span> Try Student Demo
          </v-btn>
        </v-col>
        <v-col md="2">
          <v-btn align="right" block color="primary" href="/login">
            <span class="mr-3"><v-icon>mdi-account</v-icon></span> Login
          </v-btn>
        </v-col>
      </v-row>

      <v-row class="pt-0" justify="center">
        <v-col align="center">
          <v-carousel cycle :height="CAROUSEL_HEIGHT" hide-delimiters :show-arrows="false">
            <v-carousel-item>
              <gendered-logo gender="bar" :height="CAROUSEL_HEIGHT" />
            </v-carousel-item>
            <v-carousel-item>
              <gendered-logo gender="bat" :height="CAROUSEL_HEIGHT" />
            </v-carousel-item>
            <v-carousel-item>
              <gendered-logo gender="b" :height="CAROUSEL_HEIGHT" />
            </v-carousel-item>
          </v-carousel>
        </v-col>
      </v-row>

      <beta-signup-section heading-class="pt-16" subtitle="Easy and fun laining for students and teachers">
        <v-row class="pt-6" justify="center" no-gutters>
          <v-col align="center" md="9">
            Bar/Bat Mitzvah Trainer is a browser-based study tool for students to prepare for their Bar/Bat Mitzvah with the help of a human
            teacher.
          </v-col>
        </v-row>
      </beta-signup-section>
    </v-sheet>

    <v-sheet color="grey-lighten-3" elevation="0">
      <v-row justify="center" no-gutters>
        <v-col md="6">
          <v-parallax dark :src="indexPhoto1Url" />
        </v-col>
        <v-col>
          <v-row align="center" class="pt-6 pb-4" justify="center" no-gutters>
            <v-row class="py-5" justify="center" no-gutters>
              <v-col align="center" md="8">
                <Icon icon="download-off" width="2em" />
                <h4>No installation</h4>
                Bar/Bat Mitzvah Trainer runs 100% in a browser and requires no add-ons or plugins.
              </v-col>
            </v-row>
            <v-col align="center" md="8">
              <Icon icon="account-voice" width="2em" />
              <h4>Adaptive voice recognition</h4>
              Bar/Bat Mitzvah Trainer can hear your voice and and follows along in the text with you.
            </v-col>
          </v-row>
          <v-row class="py-4" justify="center" no-gutters>
            <v-col align="center" md="8">
              <Icon icon="bar-graph" width="2em" />
              <h4>Advances with you</h4>
              Starts one word at a time, advances to longer phrases, and has you reading or singing whole sentences before long.
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-sheet>

    <v-sheet color="grey-lighten-4" elevation="0">
      <v-row justify="center" no-gutters>
        <v-col>
          <v-row align="center" class="pt-8 pb-4" justify="center" no-gutters>
            <v-col align="center" md="8">
              <Icon icon="musical-notes" width="2em" />
              <h4>Natural pitch shifting</h4>
              Sophisticated pitch shifting allows students to naturally match their vocal range with their teacher's.
            </v-col>
          </v-row>
          <v-row class="py-5" justify="center" no-gutters>
            <v-col align="center" md="8">
              <Icon icon="people-fill" width="2em" />
              <h4>Practice call and response</h4>
              Bar/Bat Mitzvah Trainer supports studying for texts that consist of call-and-response prayers, such as שיר הכבוד.
            </v-col>
          </v-row>
          <v-row class="py-5" justify="center" no-gutters>
            <v-col align="center" md="8">
              <Icon icon="calendar-date" width="2em" />
              <h4>Flexible text and calendar</h4>
              Comes built-in with entiry Tenakh text support. Ashkenazi, Sephardic, and Triennial calendar support. Ability to manually add
              texts and perform calendar overrides.
            </v-col>
          </v-row>
        </v-col>
        <v-col md="6">
          <v-parallax dark :src="indexPhoto2Url"></v-parallax>
        </v-col>
      </v-row>
    </v-sheet>

    <v-sheet class="pb-12" color="grey-lighten-2" elevation="0">
      <v-row class="pt-6" justify="center" no-gutters>
        <v-col align="center" justify="center" md="12">
          <div class="bmt" style="font-weight: bold">FROM WORDS TO TORAH</div>
        </v-col>
      </v-row>
      <v-row class="pt-6" justify="center" no-gutters>
        <v-col align="center" md="9">
          <h4>Read the words</h4>
          Students use their teacher's recordings and the accompanying text to practice reading word by word, phrase by phrase, and
          eventually full sentences.
        </v-col>
      </v-row>
      <v-row class="pt-6" justify="center" no-gutters>
        <v-col align="center" md="9">
          <h4>Sing the text</h4>
          Once students are familiar with the text, they practice singing, starting with shorter phrases, then progressing to full
          sentences.
        </v-col>
      </v-row>
      <v-row class="pt-6" justify="center" no-gutters>
        <v-col align="center" md="9">
          <h4>Torah practice</h4>
          Students gradually adapt the visual display of text to mimic the Torah, starting with nekkudot and trope until they are
          comfortable in tikkun style, without any diacritics and using a Torah font.
        </v-col>
      </v-row>
      <v-row class="pt-6" justify="center" no-gutters>
        <v-col align="center" md="9">
          <h4>...and much more</h4>
          Bar/Bat Mitzvah Trainer lets students practice b'rachot, songs, and other texts.
        </v-col>
      </v-row>
    </v-sheet>

    <v-sheet class="pb-6" color="grey-lighten-4" elevation="0">
      <beta-signup-section heading-class="pt-6" subtitle="Start today with Bar/Bat Mitzvah Trainer" />
    </v-sheet>
  </div>
</template>
